import { useCallback, useMemo, useState } from 'react';
import { orderBy } from 'lodash-es';
import { TmdxAvatar } from '@ds';
import { DropdownTypeEnum, FilterDropdown } from '@ui';
import { CSSettingsType, OrSettingsType, SuggestedSettingsEnum } from '@lp/features/RoutePlanner/types';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { filterTrialProgramSpecialists } from '@lp/features/AssignmentTables/ClinicalSpecialistsTable/utils';
import { useClinicalSpecialistsForAssignment } from '@/queries/resource';
import { useHubsQuery } from '@/queries/hub';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { getFullName } from '@/utils/helpers';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { Hub } from '@/api/apis/facilities';
import { CandidateMatchType, CaseMatchType } from '@/api/apis/resources';
import { SuggestedSelectTrigger } from './SuggestedSelectTrigger';
import { getCSSettingsText } from './utils';

type CSDropdownProps = {
  data: CSSettingsType;
  orTimeSettings: OrSettingsType;
  isDisabled: boolean;
  handleSettingsChange: (data: CSSettingsType) => void;
};

export const CSDropdown = ({ data, isDisabled, orTimeSettings, handleSettingsChange }: CSDropdownProps) => {
  const { caseId, donorType, organType, trialProgram } = useBaseCaseDetails();
  const { ClinicalSpecialist } = useRouteResources();
  const { data: csCandidates, isFetching: isCsCandidatesFetching } = useClinicalSpecialistsForAssignment({
    caseId,
    donor: donorType,
    organ: organType,
    orTime: orTimeSettings.value.selectedOr.toAbsoluteString(),
    trialProgramId: trialProgram?.id,
  });
  const [searchValue, setSearchValue] = useState('');

  const { data: hubsList } = useHubsQuery();
  const { nonEuCountryCodes } = useCountriesData();

  const hubsById = useMemo(() => new Map((hubsList?.items || []).map((hub) => [hub.id, hub])), [hubsList]);
  const candidatesById = useMemo(() => new Map((csCandidates || []).map((cs) => [cs.employeeId, cs])), [csCandidates]);

  const getShiftHub = useCallback(
    (employeeId: string, baseHub?: Hub | null): Hub | null => {
      const candidate = candidatesById.get(employeeId);
      const shiftHub = candidate ? hubsById.get(candidate.shiftHubId) : undefined;

      return shiftHub || baseHub || null;
    },
    [candidatesById, hubsById],
  );

  const csList = useMemo(() => {
    if (!csCandidates) return [];

    const csOnCaseList = ClinicalSpecialist.map((cs) => {
      const employeeFullName = getFullName(cs.data?.firstName, cs.data?.lastName) || '';
      const hub = getShiftHub(cs.resourceId, cs.data?.baseLocation);

      return {
        name: employeeFullName,
        value: cs.resourceId,
        section: 'On current case',
        data: {
          employeeId: cs.resourceId,
          employeeFullName,
          hubCode: hub?.code || '',
          hubId: hub?.id || '',
          employeeProfilePhoto: cs.data?.profilePhoto || '',
        },
      };
    });

    const otherCsList = filterTrialProgramSpecialists(csCandidates, organType, trialProgram?.id)
      .filter((cs) => {
        const isCsOnCase = csOnCaseList.find((i) => i.value === cs.employeeId);

        if (isCsOnCase) return false;

        if (cs.match.caseType !== CaseMatchType.AssignmentCandidate) return false;
        if (cs.match.matchType === CandidateMatchType.Miss) return false;

        const workingHub = hubsById.get(cs.shiftHubId);

        // A suggested route can only use a CS whose working hub is Non-EU
        return !!workingHub && nonEuCountryCodes.has(workingHub.location.country);
      })
      .map((cs) => {
        const workingHub = hubsById.get(cs.shiftHubId)!;

        return {
          name: cs.employeeFullName,
          value: cs.employeeId,
          section: 'Other',
          data: {
            employeeId: cs.employeeId,
            employeeFullName: cs.employeeFullName,
            hubCode: workingHub.code,
            hubId: workingHub.id,
            employeeProfilePhoto: cs.employeeProfilePhoto,
          },
        };
      });

    return [...csOnCaseList, ...orderBy(otherCsList, (cs) => cs.name.toLowerCase(), 'asc')];
  }, [ClinicalSpecialist, csCandidates, getShiftHub, hubsById, nonEuCountryCodes, organType, trialProgram?.id]);

  const csSearchList = useMemo(() => {
    if (searchValue) {
      return csList.filter((h) => {
        return h.name.toLowerCase().includes(searchValue.toLowerCase());
      });
    }

    return csList;
  }, [csList, searchValue]);

  return (
    <FilterDropdown
      title="CS"
      items={csSearchList}
      placement="top-start"
      type={DropdownTypeEnum.Sections}
      isDisabled={isDisabled}
      isLoading={isCsCandidatesFetching}
      selectedKeys={data.value?.employeeId ? [data.value.employeeId] : []}
      renderCustomTrigger={(isOpen) => (
        <SuggestedSelectTrigger title="CS:" value={getCSSettingsText(data.value)} isOpen={isOpen} />
      )}
      renderListBoxItem={(item) => (
        <div className="flex items-center gap-2">
          <TmdxAvatar src={item.data.employeeProfilePhoto} size="lg" radius="lg" name={item.data.employeeFullName} />
          <div className="flex-1 max-w-[170px]">
            <div className="text-m-m text-wrap">{item.data.employeeFullName}</div>
            {!!item.data.hubCode && <div className="text-s-r text-base-20 truncate">{item.data.hubCode}</div>}
          </div>
        </div>
      )}
      mode="single"
      onSearchValue={setSearchValue}
      onSelectedKeysChange={(value) => {
        const selectedValue = csSearchList.find((cs) => cs.value === value[0]);

        handleSettingsChange({
          type: SuggestedSettingsEnum.CS,
          value: selectedValue ? selectedValue.data : null,
        });
      }}
    />
  );
};
